import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, X, Car } from 'lucide-react';
import { getRecentlyViewed, clearRecentlyViewed } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to normalize image URLs
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Format price
const formatPrice = (price) => {
  if (!price || price === 0) return "Appeler";
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
  }).format(price);
};

export const RecentlyViewed = ({ currentVehicleId = null }) => {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    const recent = getRecentlyViewed();
    // Filter out current vehicle if on a detail page
    const filtered = currentVehicleId 
      ? recent.filter(v => v.id !== currentVehicleId)
      : recent;
    setVehicles(filtered.slice(0, 4));
  }, [currentVehicleId]);

  if (vehicles.length === 0) return null;

  return (
    <div 
      className={`fixed bottom-20 right-4 z-40 transition-all duration-300 hidden sm:block ${isMinimized ? 'w-12' : 'w-64'}`}
      data-testid="recently-viewed-widget"
    >
      {isMinimized ? (
        <button
          onClick={() => setIsMinimized(false)}
          className="w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
          title={t('recentlyViewed.title')}
        >
          <Clock className="h-5 w-5 text-[#3b82f6]" />
          <span className="absolute -top-1 -right-1 bg-[#3b82f6] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
            {vehicles.length}
          </span>
        </button>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#3b82f6]" />
              <span className="text-xs font-semibold text-slate-700">{t('recentlyViewed.badge')}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  clearRecentlyViewed();
                  setVehicles([]);
                }}
                className="text-slate-400 hover:text-slate-600 text-[10px]"
                title="Effacer l'historique"
              >
                Effacer
              </button>
              <button
                onClick={() => setIsMinimized(true)}
                className="text-slate-400 hover:text-slate-600 p-1"
                title="Réduire"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          
          {/* Vehicle List */}
          <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {vehicles.map((vehicle) => (
              <Link
                key={vehicle.id}
                to={`/vehicle/${vehicle.id}`}
                className="flex items-center gap-3 p-2.5 hover:bg-slate-50 transition-colors"
              >
                <div className="w-14 h-10 rounded overflow-hidden bg-slate-100 flex-shrink-0">
                  {vehicle.image ? (
                    <img 
                      src={getImageUrl(vehicle.image)} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Car className="h-4 w-4 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-800 truncate">
                    {vehicle.year} {vehicle.make}
                  </p>
                  <p className="text-[10px] text-slate-500 truncate">{vehicle.model}</p>
                </div>
                <span className="text-xs font-semibold text-[#3b82f6] flex-shrink-0">
                  {formatPrice(vehicle.price)}
                </span>
              </Link>
            ))}
          </div>
          
          {/* Footer */}
          <div className="bg-slate-50 px-3 py-2 border-t border-slate-200">
            <Link 
              to="/inventory" 
              className="text-[10px] text-[#3b82f6] hover:underline font-medium"
            >
              Voir tout l'inventaire →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
